'use client'

import { useState, useContext, useEffect } from 'react'
import {
  Dialog,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
  CircleStackIcon,
  UserCircleIcon,
  UsersIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  MapIcon,
  ClipboardDocumentListIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  BellIcon,
  HeartIcon,
  DocumentTextIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { AppContext } from '../context/AppContext';

const baseSettingsMenuItems = [
  { name: 'Obituaries', description: 'Manage obituaries and memorial pages.', href: '/obituaries', icon: HeartIcon, feature: 'obituaries' },
  { name: 'Documents', description: 'Create and manage document templates for contracts, invoices, and letters.', href: '/documents/templates', icon: DocumentTextIcon, feature: 'documents' },
  { name: 'Analytics', description: 'View charts and reports on cemetery data.', href: '/analytics', icon: ChartBarIcon, feature: 'analytics' }
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { token, user, expireToken, siteName, headerLogo, portalEnabled, hasFeature } = useContext(AppContext);

  useEffect(() => {
    if (!token) return;
    const fetchCount = async () => {
      try {
        const res = await fetch('/api/notifications/unread-count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count);
        }
      } catch (e) { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isGroundsCrew = user?.role === 'grounds_crew';
  const homeLink = isGroundsCrew ? '/work-orders' : (portalEnabled ? '/public' : '/');

  const settingsMenuitems = [
    ...baseSettingsMenuItems.filter(item => !item.feature || hasFeature(item.feature)),
    ...(isAdmin ? [
      { name: 'Data', description: 'Export and import all data as CSV files in a ZIP archive.', href: '/data', icon: CircleStackIcon },
      { name: 'Geospatial Import', description: 'Import plot geometry from KMZ, Shapefile, GeoJSON, or CSV.', href: '/geospatial-import', icon: MapIcon },
      { name: 'Public Portal', description: 'Configure the public-facing cemetery portal.', href: '/public-portal-settings', icon: GlobeAltIcon },
      { name: 'Site Settings', description: 'Configure site name, branding, and custom fields.', href: '/site-settings', icon: WrenchScrewdriverIcon }
    ] : [])
  ];

  const userMenuItems = [
    { name: 'Notifications', description: 'View your notification history', href: '/notifications', icon: BellIcon, badge: unreadCount },
    { name: 'Account Settings', description: 'Update your profile and password', href: '/settings', icon: Cog6ToothIcon },
    ...(isAdmin ? [{ name: 'User Management', description: 'Manage user accounts and permissions', href: '/users', icon: UsersIcon }] : []),
    ...(isSuperAdmin ? [{ name: 'Tenant Management', description: 'Manage tenant sites and configurations', href: '/tenants', icon: BuildingOffice2Icon }] : [])
  ];

  return (token) ? (
    <header className="bg-white">
      <nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
        <div className="flex lg:flex-1">
          <a href={homeLink} className="-m-1.5 p-1.5 flex items-center gap-x-3">
            <span className="sr-only">{siteName}</span>
            <img
              alt={siteName}
              src={headerLogo}
              className="h-10 w-auto"
            />
            <span className="text-lg font-semibold text-gray-900">{siteName}</span>
          </a>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          {!isGroundsCrew && (
            <>
              <a href="/plots" className="text-sm/6 font-semibold text-gray-900">
                Plots
              </a>
              <a href="/contacts" className="text-sm/6 font-semibold text-gray-900">
                Contacts
              </a>
              {hasFeature('sales') && (
                <a href="/sales" className="text-sm/6 font-semibold text-gray-900">
                  Sales
                </a>
              )}
              {hasFeature('products') && (
                <a href="/products" className="text-sm/6 font-semibold text-gray-900">
                  Services
                </a>
              )}
            </>
          )}
          {hasFeature('workOrders') && (
            <a href="/work-orders" className="text-sm/6 font-semibold text-gray-900">
              Work Orders
            </a>
          )}
          {!isGroundsCrew && (
            <Popover className="relative">
              <PopoverButton className="flex items-center gap-x-1 text-sm/6 font-semibold text-gray-900">
                More
                <ChevronDownIcon aria-hidden="true" className="size-5 flex-none text-gray-400" />
              </PopoverButton>

              <PopoverPanel
                transition="true"
                className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
              >
                <div className="p-4">
                  {settingsMenuitems.map((item) => (
                    <div
                      key={item.name}
                      className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-gray-50"
                    >
                      <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white">
                        <item.icon aria-hidden="true" className="size-6 text-gray-600 group-hover:text-brand-green-600" />
                      </div>
                      <div className="flex-auto">
                        <a href={item.href} className="block font-semibold text-gray-900">
                          {item.name}
                          <span className="absolute inset-0" />
                        </a>
                        <p className="mt-1 text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverPanel>
            </Popover>
          )}
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-6">
          <Popover className="relative">
            <PopoverButton className="flex items-center gap-x-1 text-sm/6 font-semibold text-gray-900 relative">
              <UserCircleIcon className="size-6 text-gray-400" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <span className="hidden xl:inline">{user?.firstName || user?.email}</span>
              <ChevronDownIcon aria-hidden="true" className="size-5 flex-none text-gray-400" />
            </PopoverButton>

            <PopoverPanel
              transition="true"
              className="absolute right-0 top-full z-10 mt-3 w-80 overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <div className="p-4">
                <div className="mb-4 px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'User'}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  <span className="mt-1 inline-flex items-center rounded-full bg-brand-green-100 px-2 py-0.5 text-xs font-medium text-brand-green-700">
                    {user?.role}
                  </span>
                </div>
                {userMenuItems.map((item) => (
                  <div
                    key={item.name}
                    className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm/6 hover:bg-gray-50"
                  >
                    <div className="flex size-11 flex-none items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white relative">
                      <item.icon aria-hidden="true" className="size-6 text-gray-600 group-hover:text-brand-green-600" />
                      {item.badge > 0 && (
                        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex-auto">
                      <a href={item.href} className="block font-semibold text-gray-900">
                        {item.name}
                        <span className="absolute inset-0" />
                      </a>
                      <p className="mt-1 text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
                <div className="mt-2 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={expireToken}
                    className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 text-left"
                  >
                    Log out
                  </button>
                </div>
              </div>
            </PopoverPanel>
          </Popover>
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href={homeLink} className="-m-1.5 p-1.5 flex items-center gap-x-2">
              <span className="sr-only">{siteName}</span>
              <img
                alt={siteName}
                src={headerLogo}
                className="h-8 w-auto"
              />
              <span className="text-base font-semibold text-gray-900">{siteName}</span>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {!isGroundsCrew && (
                  <>
                    <a
                      href="/plots"
                      className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      Plots
                    </a>
                    <a
                      href="/contacts"
                      className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      Contacts
                    </a>
                    {hasFeature('sales') && (
                      <a
                        href="/sales"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Sales
                      </a>
                    )}
                    {hasFeature('products') && (
                      <a
                        href="/products"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Services
                      </a>
                    )}
                  </>
                )}
                {hasFeature('workOrders') && (
                  <a
                    href="/work-orders"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Work Orders
                  </a>
                )}
                {!isGroundsCrew && (
                  <Disclosure as="div" className="-mx-3">
                    <DisclosureButton className="group flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50">
                      More
                      <ChevronDownIcon aria-hidden="true" className="size-5 flex-none group-data-[open]:rotate-180" />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-2 space-y-2">
                      {settingsMenuitems.map((item) => (
                        <DisclosureButton
                          key={item.name}
                          as="a"
                          href={item.href}
                          className="block rounded-lg py-2 pl-6 pr-3 text-sm/7 font-semibold text-gray-900 hover:bg-gray-50"
                        >
                          {item.name}
                        </DisclosureButton>
                      ))}
                    </DisclosurePanel>
                  </Disclosure>
                )}
              </div>
              <div className="py-6 space-y-2">
                <div className="px-3 py-2 border-b border-gray-100 mb-2">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'User'}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                <a
                  href="/notifications"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 flex items-center justify-between"
                >
                  Notifications
                  {unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </a>
                <a
                  href="/settings"
                  className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Account Settings
                </a>
                {isAdmin && (
                  <a
                    href="/users"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    User Management
                  </a>
                )}
                {isSuperAdmin && (
                  <a
                    href="/tenants"
                    className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Tenant Management
                  </a>
                )}
                <button
                  type="button"
                  onClick={expireToken}
                  className="-mx-3 block w-full text-left rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Log out
                </button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  ) : null;
}
